/* eslint-disable react/no-danger */
import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { PDFDownloadLink } from '@react-pdf/renderer';
import BoxedContainer from './layouts/BoxedContainer';
import Sprites from '../components/Sprites';
import { getTranslationByKey } from '../helpers/translation-helper';
import { DWTCContext } from '../utils/context';
import FreeZoneActivityTablePdf from './FreeZoneActivityTablePdf';
import AutoDownloadPDF from '../components/AutoDownloadPDF';

export default function FreeZoneActivityTable({ id, data }) {
  const title = _.get(data, 'title', '');
  const text = _.get(data, 'text', '');
  const subtitle = _.get(data, 'subtitle', '');
  const placeholderText = _.get(data, 'placeholder', '');

  const [documentGenerated, setDocumentGenerated] = useState(false);
  const [search, setSearch] = useState('');
  const [toggle, setToggle] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [filteredData, setFilteredData] = useState({ columnNames: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [selectedLicenseType, setSelectedLicenseType] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [visibleRowCount, setVisibleRowCount] = useState(9);
  const { translations } = useContext(DWTCContext);

  const handleToggle = () => setToggle((prev) => !prev);

  const handleSearch = () => {
    if (!search.trim()) {
      setFilteredData(originalData); // reset to original data if Search is empty
    } else {
      const filteredRows = originalData.rows.filter((row) =>
        row.some(
          (cell) =>
            cell !== null &&
            cell !== undefined &&
            cell.toString().toLowerCase().includes(search.toLowerCase()),
        ),
      );
      setFilteredData({ ...originalData, rows: filteredRows });
    }
    setCurrentPage(0); // Reset to first page on Search
  };

  const filterData = () => {
    if (originalData) {
      const filteredRows = originalData.rows.filter((row) => {
        const licenseTypeIndex = originalData.columnNames.indexOf('LICENSE TYPE');
        return (
          // (selectedActivityGroup ? row[activityGroupIndex] === selectedActivityGroup : true) &&
          (selectedLicenseType ? row[licenseTypeIndex] === selectedLicenseType : true) &&
          (search
            ? row.some((cell) => cell?.toString().toLowerCase().includes(search.toLowerCase()))
            : true)
        );
      });

      setDocumentGenerated(false);
      setFilteredData({ ...originalData, rows: filteredRows });
      setCurrentPage(0); // Reset to the first page after filtering
    }
  };

  const handleSearchInput = (e) => setSearch(e.target.value);

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') filterData();
  };

  const handleLicenseTypeChange = (e) => {
    setSelectedLicenseType(e.target.value);
  };

  const totalPages = Math.ceil(filteredData.rows.length / rowsPerPage);
  const currentRows = filteredData.rows.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );

  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 0));
  const goToPage = (page) => setCurrentPage(page);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumVisible = 3;
    let startPage = Math.max(currentPage - 2, 0);
    let endPage = startPage + maxPageNumVisible;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(0, endPage - maxPageNumVisible);
    }

    if (startPage > 0) {
      pageNumbers.push(
        <button type="button" key="first" onClick={() => goToPage(0)} className="border px-3 py-1">
          1
        </button>,
        <span key="ellipsis1" className="py-1 min-h-[40px]">
          ...
        </span>,
      );
    }

    for (let i = startPage; i < endPage; i += 1) {
      pageNumbers.push(
        <button
          type="button"
          key={i}
          onClick={() => goToPage(i)}
          className={`px-2 py-1 border
          ${
            currentPage === i
              ? 'text-white bg-red border-red'
              : 'text-[#333] bg-white border-[#E6E6E6]'
          }`}>
          {i + 1}
        </button>,
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis2" className="py-1 min-h-[40px]">
          ...
        </span>,
        <button
          type="button"
          key="last"
          onClick={() => goToPage(totalPages - 1)}
          className="px-3 py-1 border">
          {totalPages}
        </button>,
      );
    }

    return pageNumbers;
  };

  const handleGenerateDocument = () => {
    if (!_.isEmpty(filteredData.rows)) {
      setDocumentGenerated(true);
    }
  };

  const handleShowMore = () => {
    setVisibleRowCount((prevCount) => prevCount + 9);
  };

  useEffect(() => {
    filterData();
  }, [selectedLicenseType, search]);

  useEffect(() => {
    fetch('https://freezone.dwtc.workers.dev/')
      .then((response) => response.json())
      .then((tdata) => {
        setOriginalData(tdata.items[0]);
        setFilteredData(tdata.items[0]);

        const uniqueLicenseTypes = new Set(
          tdata.items[0].rows.map((row) => row[tdata.items[0].columnNames.indexOf('LICENSE TYPE')]),
        );
        setLicenseTypes([...uniqueLicenseTypes]);

        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch data:', err);
        setError(error);
        setLoading(false);
      });
  }, []);

  return (
    <section id={id}>
      <div className="bg-white pt-12.5 lg:pt-25 pb-10 lg:pb-12.5">
        <BoxedContainer>
          <div className="w-full grid grid-cols-6 lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="col-span-6 lg:col-span-12 flex flex-col items-center gap-5">
              <div
                className="child:text-clamp20to40
                child:ltr:!font-URWDINMedium child:rtl:!font-GESSTwoDWTCMedium
                child:leading-[1.3]
                last:child:!m-0 child:text-center text-[#58595B] md:text-[#4D4D4D]"
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <div className="py-px bg-red w-10 lg:w-12.5" />
              <div
                className="child:text-[#58595B] child:md:text-[#4D4D4D]
                child:text-clamp12to18
                child:ltr:!font-URWDINRegular child:rtl:!font-GESSTwoDWTCLight
                child:ltr:leading-[1.6] child:rtl:leading-[1.4] child:rtl:md:leading-[1.5]
                last:child:!m-0 child:text-center col-span-6"
                dangerouslySetInnerHTML={{ __html: text }}
              />
            </div>
          </div>
        </BoxedContainer>
      </div>

      <div className="bg-desertStorm pt-7.5 pb-6 lg:py-12.5">
        <BoxedContainer>
          <div className="w-full grid grid-cols-6 lg:grid-cols-12 gap-6 lg:gap-10">
            <div className="col-span-6 lg:col-span-12 flex flex-col items-center gap-5">
              <div
                className="child:text-[#58595B] child:md:text-[#231F20] child:text-clamp15to19
                child:!font-LibreBaskervilleRegular rtl:p-child:font-[600]
                child:ltr:leading-[1.6] child:rtl:leading-[1.4] child:rtl:md:leading-[1.5]
                last:child:!m-0 child:text-center col-span-6"
                dangerouslySetInnerHTML={{ __html: subtitle }}
              />
            </div>
            <div
              className="col-span-6 lg:col-span-12 flex flex-col items-center gap-5 lg:grid
              lg:grid-cols-12">
              <div
                className="w-full lg:col-span-4 text-clamp14to18
                ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight md:font-URWDINMedium
                text-[#58595B] md:text-[#231F20] ltr:md:text-left
                rtl:md:text-right text-center">
                {placeholderText}
              </div>
              <input
                placeholder={getTranslationByKey(
                  translations,
                  'Search for an activity, license or keyword',
                )}
                className="py-3.5 px-2.5 w-full
                ltr:lg:col-span-5 rtl:lg:col-span-4
                rtl:font-URWDINRegular rtl:text-[12px] rtl:md:text-[15px]"
                value={search}
                onChange={handleSearchInput}
                onKeyDown={handleSearchKeyPress}
              />
              <button
                type="button"
                onClick={handleToggle}
                className="w-full hidden lg:flex justify-between items-center ltr:md:pl-[10px]
                rtl:md:pr-[10px] relative rtl:lg:col-span-2 ">
                <div className="flex items-center gap-2">
                  <Sprites name="filter-black" className="min-w-5 min-h-5" />
                  <span
                    className="text-[14px] text-oil uppercase
                  rtl:tracking-[0px] rtl:font-GESSTwoDWTCLight">
                    {getTranslationByKey(translations, 'Filters')}
                  </span>
                </div>
                <span
                  className={`absolute bottom-[-66px] left-1/2 right-1/2
                  ${toggle ? 'hidden lg:block' : 'hidden'}`}>
                  <Sprites name="filter-arrow" />
                </span>
              </button>
              <div
                className="hidden lg:col-span-2 lg:flex w-full ltr:md:pl-[10px]
                rtl:md:pr-[10px]">
                <button
                  type="button"
                  onClick={handleSearch}
                  className="py-3 pt-4 px-12 md:px-14 bg-red rounded-full text-white tracking-widest
                  w-full uppercase rtl:tracking-[0px]
                  ltr:font-URWDINBold rtl:font-GESSTwoDWTCMedium">
                  {getTranslationByKey(translations, 'Search')}
                </button>
              </div>
            </div>
          </div>
        </BoxedContainer>
      </div>
      <div className="py-4 bg-darkGray lg:hidden">
        <BoxedContainer>
          <button
            type="button"
            onClick={handleToggle}
            className="w-full flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Sprites name="filter" />
              <span
                className="text-[14px] text-white uppercase rtl:tracking-[0px]
              rtl:font-GESSTwoDWTCLight">
                {getTranslationByKey(translations, 'Filters')}
              </span>
            </div>
          </button>
        </BoxedContainer>
      </div>
      <div className="bg-darkGray data-[toggle=false]:!hidden" data-toggle={toggle}>
        <BoxedContainer>
          <div className="pt-5 pb-7.5 md:py-7.5">
            <div className="flex justify-center lg:justify-between items-center">
              <span
                className="text-white uppercase text-clamp14to16 hidden lg:inline
              rtl:font-GESSTwoDWTCLight">
                {getTranslationByKey(translations, 'Filters')}
              </span>
              <div className="flex flex-col lg:flex-row w-full lg:w-fit lg:items-center gap-7">
                <label
                  className="flex w-full justify-between items-center gap-6 text-silver relative
                    after:content-[''] lg:w-fit after:absolute after:ltr:right-3.5
                    after:rtl:left-3.5after:top-1/2
                    after:!block after:-translate-y-1/2 after:text-white after:pointer-events-none
                    after:w-2 after:h-2 after:rotate-[135deg] after:border-t-2 after:border-r-2
                    after:border-silver">
                  <span
                    className="whitespace-nowrap rtl:font-GESSTwoDWTCLight ltr:font-URWDINRegular
                    text-white">
                    {getTranslationByKey(translations, 'License Type')}
                  </span>
                  <select
                    className="min-w-39.5 py-[10px] md:py-3.5 rtl:pr-2.5 rtl:pl-8 ltr:pl-2.5
                    ltr:pr-8 max-w-[200px] w-full border border-silver bg-transparent text-silver
                    whitespace-nowrap rtl:font-GESSTwoDWTCLight ltr:font-URWDINRegular text-[11px]
                    md:text-[13px]"
                    id="licenseType"
                    value={selectedLicenseType}
                    onChange={handleLicenseTypeChange}>
                    <option value="">{getTranslationByKey(translations, 'Please Select')}</option>
                    {licenseTypes
                      .filter((type) => type)
                      .map(
                        (type) =>
                          type && (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ),
                      )}
                  </select>
                </label>
              </div>
            </div>
          </div>
        </BoxedContainer>
      </div>

      <BoxedContainer className="flex justify-center pt-6 lg:hidden">
        <button
          type="button"
          onClick={handleSearch}
          className="py-3 flex justify-center items-center pt-4 px-12
          bg-red rounded-full text-white uppercase tracking-widest w-fit
          hover:bg-[#B53B50] duration-300
          ltr:!font-URWDINBold rtl:!font-GESSTwoDWTCMedium font-bold
          text-[11px] h-[33px] rtl:tracking-[0px]">
          {getTranslationByKey(translations, 'Search')}
        </button>
      </BoxedContainer>

      <div className="bg-white pt-6 pb-10 lg:pb-16 lg:pt-12.5" id="tableDataContainer">
        {loading && <p className="text-center">Loading...</p>}
        {error && <p className="text-center">Error loading data!</p>}
        {!loading && !error && filteredData && filteredData.rows.length === 0 ? (
          <p className="text-center">{getTranslationByKey(translations, 'No data found')}!</p>
        ) : (
          <>
            <div className="hidden lg:block">
              <table className="w-full !max-w-292.5 bg-desertStorm mx-auto" dir="ltr">
                <thead className="border-b border-red">
                  <tr>
                    {filteredData.columnNames.map((name, index) => (
                      <th
                        key={index}
                        className="p-4 ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        whitespace-nowrap text-start [&:nth-child(2)]:w-[185px]
                        [&:last-child]:w-[155px]">
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((row, index) => (
                    <tr
                      data-is-odd={(currentPage * currentRows + index) % 2 === 0}
                      className="border border-[#E6E6E6] border-t-0 data-[is-odd=true]:bg-white">
                      {row.map((item, index2) => (
                        <td
                          key={index2}
                          className="px-4 py-7 text-clamp10to14 text-liver text-start">
                          {item === null ? '-' : item}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                className="flex justify-center w-auto gap-4 lg:py-[54px] mx-auto
                [&>button]:min-w-[40px]
                [&>button]:ltr:font-URWDINRegular [&>button]:rtl:font-GESSTwoDWTCLight">
                <button
                  className={`ltr:!font-URWDINMedium rtl:!font-GESSTwoDWTCMedium
                  ${currentPage === 0 ? 'cursor-not-allowed opacity-50' : ''}`}
                  type="button"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 0}>
                  {getTranslationByKey(translations, 'Previous')}
                </button>
                {renderPageNumbers()}
                <button
                  className={`ltr:!font-URWDINMedium rtl:!font-GESSTwoDWTCMedium
            ${currentPage === totalPages - 1 ? 'cursor-not-allowed opacity-50' : ''}`}
                  type="button"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages - 1}>
                  {getTranslationByKey(translations, 'Next')}
                </button>
              </div>
            </div>

            <div className="block lg:hidden overflow-x-auto">
              <table className="w-full bg-desertStorm mx-auto" dir="ltr">
                <thead className="border-b-[2px] border-red">
                  <tr>
                    {filteredData.columnNames.map((name, indexM) => (
                      <th
                        key={indexM}
                        className="px-2 py-4
                      ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                      whitespace-nowrap text-start
                      text-[12px] max-w-[33.3333%] text-[#58595B]">
                        {filteredData.columnNames[indexM]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.rows.slice(0, visibleRowCount).map((row, index) => (
                    <tr
                      key={index}
                      className="border border-[#E6E6E6] border-t-0 w-full 
                    data-[is-odd=true]:bg-white">
                      <td
                        className="px-[12px] py-4
                        ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        text-clamp10to14 text-liver
                        text-start border border-[#E6E6E6] leading-1.25">
                        {row[0]}
                      </td>
                      <td
                        className="px-[12px] py-4
                        ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        text-clamp10to14 text-liver
                        text-start border border-[#E6E6E6] leading-1.25">
                        {row[1]}
                      </td>
                      <td
                        className="px-[12px] py-4
                        ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        text-clamp10to14 text-liver
                        text-start border border-[#E6E6E6] leading-1.25">
                        {row[2]}
                      </td>
                      <td
                        className="px-[12px] py-4
                        ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        text-clamp10to14 text-liver
                        text-start border border-[#E6E6E6] leading-1.25">
                        {row[3]}
                      </td>
                      <td
                        className="px-[12px] py-4
                        ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        text-clamp10to14 text-liver
                        text-start border border-[#E6E6E6] leading-1.25">
                        {row[4]}
                      </td>
                      <td
                        className="px-[12px] py-4
                        ltr:font-URWDINRegular rtl:font-GESSTwoDWTCLight
                        text-clamp10to14 text-liver
                        text-start border border-[#E6E6E6] leading-1.25">
                        {row[5]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visibleRowCount <= filteredData.rows.length ? (
                <button
                  type="button"
                  className="pt-8 flex flex-row gap-[15px] justify-center items-center w-full"
                  onClick={handleShowMore}>
                  <Sprites name="show-more" />
                  <span
                    className="text-[14px] ltr:font-URWDINMedium font-medium leading-[100%]
                text-[#4a4a4a] uppercase rtl:font-GESSTwoDWTCLight">
                    {getTranslationByKey(translations, 'Show More')}
                  </span>
                </button>
              ) : (
                ''
              )}
            </div>
          </>
        )}
        <div className="w-full flex justify-center mt-8 lg:mt-0">
          {documentGenerated ? (
            <PDFDownloadLink
              document={
                <FreeZoneActivityTablePdf
                  data={{ rows: filteredData.rows, columnNames: filteredData.columnNames }}
                  selectedFilter={selectedLicenseType}
                  keywords={search}
                />
              }
              fileName="activities-licences.pdf">
              {(state) => {
                if (state.loading) {
                  return (
                    <p
                      className="text-center py-3 pt-4 px-12 md:px-14 text-red uppercase
                      rtl:tracking-[0px] ltr:font-URWDINBold rtl:font-GESSTwoDWTCMedium">
                      {getTranslationByKey(translations, 'Loading')}...
                    </p>
                  );
                }
                return <AutoDownloadPDF blobData={state} callback={setDocumentGenerated} />;
              }}
            </PDFDownloadLink>
          ) : (
            <button
              type="button"
              disabled={_.isEmpty(filteredData.rows)}
              onClick={handleGenerateDocument}
              className="py-3 pt-4 px-12 md:px-14 bg-white rounded-full text-red tracking-widest
              w-fit uppercase rtl:tracking-[0px] ltr:font-URWDINBold rtl:font-GESSTwoDWTCMedium
              border border-red lg:hover:bg-red lg:hover:text-white transition-all duration-300
              disabled:opacity-50 disabled:pointer-events-none">
              {getTranslationByKey(translations, 'Download Table')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
